import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useDebounce } from "@/hooks/useDebounce";
import { fuzzySearchListings, expandSearchQuery } from "@/utils/fuzzySearch";

export interface ListingSuggestion {
  id: string;
  title: string;
  description: string;
  car_brand: string;
  car_model: string;
  part_number: string | null;
  price: number;
  images: string[] | null;
}

const escapeILike = (value: string) => value.replace(/[%_,()]/g, "");

/**
 * Live-sökförslag för annonser.
 * Återanvänder plattformens befintliga fuzzy-sökning (fuzzySearchListings/expandSearchQuery)
 * så att felstavningar och delvisa sökningar fungerar likadant som på söksidan.
 */
export const useListingSuggestions = (query: string, limit = 5) => {
  const debouncedQuery = useDebounce(query, 250);
  const [suggestions, setSuggestions] = useState<ListingSuggestion[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = debouncedQuery.trim();

    if (term.length < 2 || term.startsWith("@")) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);

      const tokens = Array.from(
        new Set(
          expandSearchQuery(term)
            .flatMap((q) => q.split(/\s+/))
            .map((t) => escapeILike(t.trim()))
            .filter((t) => t.length >= 2)
        )
      ).slice(0, 6);

      let request = supabase
        .from("listings")
        .select("id, title, description, car_brand, car_model, part_number, price, images")
        .eq("status", "active")
        .is("removed_at", null)
        .order("created_at", { ascending: false })
        .limit(60);

      if (tokens.length > 0) {
        const filters = tokens.flatMap((t) => [
          `title.ilike.%${t}%`,
          `car_brand.ilike.%${t}%`,
          `car_model.ilike.%${t}%`,
          `part_number.ilike.%${t}%`,
          `description.ilike.%${t}%`,
        ]);
        request = request.or(filters.join(","));
      }

      const { data, error } = await request;
      if (cancelled) return;

      if (error || !data) {
        setSuggestions([]);
        setLoading(false);
        return;
      }

      const candidates = data as ListingSuggestion[];

      // Ranka med samma fuzzy-logik som söksidan
      const matched = new Map<string, ListingSuggestion>();
      for (const expanded of expandSearchQuery(term)) {
        for (const match of fuzzySearchListings(candidates, expanded, 15)) {
          if (!matched.has(match.id)) matched.set(match.id, match);
        }
      }

      const ranked = fuzzySearchListings(Array.from(matched.values()), term, 0);

      setSuggestions(ranked.slice(0, limit));
      setLoading(false);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, limit]);

  return { suggestions, loading };
};
